import * as React from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  BackLink,
  Card,
  CardBody,
  CardHead,
  KV,
  LastUpdated,
  NavRail,
  PageHeader,
} from "@ardept/design-system";
import { useTenant, useMe } from "../api/queries.js";
import { railVendorName } from "../auth/railVendorName.js";
import "./TenantDetailScreen.css";
import "./screens.css";

export function TenantDetailScreen(): JSX.Element {
  const { tenantId } = useParams<{ tenantId: string }>();
  const navigate = useNavigate();
  const { data: tenant, isLoading, error, dataUpdatedAt } = useTenant(tenantId!);
  const { data: me } = useMe();

  // Build operator display for NavRail from /me response
  const operatorDisplay = {
    name: me?.email
      ? (me.email.split("@")[0] ?? me.email)
          .replace(/[._-]/g, " ")
          .replace(/\b\w/g, (c) => c.toUpperCase())
      : "Operator",
    email: me?.email ?? "",
  };

  if (isLoading && !tenant) {
    return (
      <div className="app">
        <NavRail active="tenants" operator={operatorDisplay} vendorName={railVendorName(me)} onNavigate={navigate} />
        <main className="main">
          <div className="detail-loading">Loading tenant...</div>
        </main>
      </div>
    );
  }

  if (error) {
    return (
      <div className="app">
        <NavRail active="tenants" operator={operatorDisplay} vendorName={railVendorName(me)} onNavigate={navigate} />
        <main className="main">
          <BackLink to="/tenants" navigate={navigate} label="Back to tenants" />
          <div className="detail-error">
            Tenant not found, or you do not have access. Check the URL or your tenant scope.
          </div>
        </main>
      </div>
    );
  }

  if (!tenant) return <></>;

  const config = tenant.tenant_config ?? {};
  const channels = config.channels ?? {};
  const webhooks = config.webhooks ?? {};
  const channelEntries = Object.entries(channels);
  const webhookEntries = Object.entries(webhooks);

  return (
    <div className="app">
      <NavRail active="tenants" operator={operatorDisplay} vendorName={railVendorName(me)} onNavigate={navigate} />
      <main className="main">
        <BackLink to="/tenants" navigate={navigate} label="Back to tenants" />
        <PageHeader
          title={tenant.display_name}
          subtitle={<><span className="mono">{tenant.slug}</span> - {tenant.operator_count} operators</>}
          actions={dataUpdatedAt ? <LastUpdated timestamp={dataUpdatedAt} /> : undefined}
        />

        <div className="card-grid">
          {/* Overview card */}
          <Card>
            <CardHead>Overview</CardHead>
            <CardBody>
              <div className="kv-list">
                <KV label="Slug" value={<code className="mono">{tenant.slug}</code>} />
                <KV label="Created" value={new Date(tenant.created_at).toLocaleString()} />
                <KV label="Operators" value={tenant.operator_count} />
                <KV
                  label="Models allowlisted"
                  value={config.model_id_allowlist?.length ?? 0}
                />
                <KV label="Channels configured" value={channelEntries.length} />
              </div>
            </CardBody>
          </Card>

          {/* Model allowlist card (only when non-empty) */}
          {config.model_id_allowlist && config.model_id_allowlist.length > 0 ? (
            <Card>
              <CardHead>Model allowlist</CardHead>
              <CardBody>
                <ul className="model-list">
                  {config.model_id_allowlist.map((m) => (
                    <li key={m}>
                      <code className="mono">{m}</code>
                    </li>
                  ))}
                </ul>
              </CardBody>
            </Card>
          ) : null}

          {/* Per-channel cards — T-16-06-XSS: values rendered as React children (auto-escaped);
              webhook URLs are in <code> blocks and NOT made clickable (no anchor). */}
          {channelEntries.map(([name, ch]) => (
            <Card key={`channel-${name}`}>
              <CardHead>Channel: {name}</CardHead>
              <CardBody>
                <div className="kv-list">
                  {ch.from ? <KV label="From" value={ch.from} /> : null}
                  {ch.from_name ? <KV label="From name" value={ch.from_name} /> : null}
                  {ch.reply_to ? <KV label="Reply to" value={ch.reply_to} /> : null}
                  {ch.allowed_sender_domains && ch.allowed_sender_domains.length > 0 ? (
                    <KV
                      label="Allowed sender domains"
                      value={ch.allowed_sender_domains.join(", ")}
                    />
                  ) : null}
                  {ch.scope ? <KV label="Default scope" value={ch.scope} /> : null}
                  {ch.default_channel_id ? (
                    <KV
                      label="Default channel id"
                      value={<code className="mono">{ch.default_channel_id}</code>}
                    />
                  ) : null}
                  {ch.azure_ad_tenant_id ? (
                    <KV
                      label="Azure AD tenant id"
                      value={<code className="mono">{ch.azure_ad_tenant_id}</code>}
                    />
                  ) : null}
                </div>
              </CardBody>
            </Card>
          ))}

          {/* Per-webhook cards — T-16-06-XSS: URLs shown in <code>, NOT as anchor links */}
          {webhookEntries.map(([name, wh]) => (
            <Card key={`webhook-${name}`}>
              <CardHead>Webhook: {name}</CardHead>
              <CardBody>
                <div className="kv-list">
                  {wh.url ? (
                    <KV label="URL" value={<code className="mono">{wh.url}</code>} />
                  ) : null}
                  {wh.path ? (
                    <KV label="Path" value={<code className="mono">{wh.path}</code>} />
                  ) : null}
                </div>
              </CardBody>
            </Card>
          ))}
        </div>

        <p className="footnote">
          Editing is CLI-only in v1.3. Run{" "}
          <code className="mono">ardept tenant config get {tenant.slug}</code> for raw config.
        </p>
      </main>
    </div>
  );
}
